import type {Lang} from '@product/quies/lib/hreflang'
import type {SessionWalkthroughProps} from '@product/quies/components/SessionWalkthrough/SessionWalkthrough'
import copy from '@product/quies/proof/copy.json'

// Omit sectionTitle — the home passes its own.
type WalkthroughContent = Omit<SessionWalkthroughProps, 'sectionTitle'>

// The walkthrough lives on the homes only (search estate plan, decision 8): as the first section of the feature page.
type ConditionKey = 'generic'

// ---------------------------------------------------------------------------
// Frame titles — consistent across conditions within a language
// ---------------------------------------------------------------------------

const frameTitles: Record<Lang, [string, string, string]> = {
    en: ['Light and intuitive', 'Go deeper if you choose', 'Your session is written for this moment'],
    fr: ['Léger et intuitif', 'Allez plus loin si vous le souhaitez', 'Votre séance est écrite pour cet instant'],
    de: ['Leicht und intuitiv', 'Gehen Sie tiefer, wenn Sie möchten', 'Ihre Sitzung wird für diesen Moment geschrieben'],
    es: ['Ligero e intuitivo', 'Profundiza si lo deseas', 'Tu sesión se escribe para este momento'],
    ja: copy.ja.walkthrough.frameTitles as [string, string, string],
    zh: copy.zh.walkthrough.frameTitles as [string, string, string],
}

// ---------------------------------------------------------------------------
// Shuffle hints
// ---------------------------------------------------------------------------

const shuffleHints: Record<Lang, string> = {
    en: "Didn't resonate? Shuffle for a different angle.",
    fr: 'Ça ne résonne pas\u00a0? Explorez un autre chemin.',
    de: 'Passt nicht? Versuchen Sie einen anderen Ansatz.',
    es: '¿No resuena? Prueba desde otro ángulo.',
    ja: copy.ja.walkthrough.shuffleHint,
    zh: copy.zh.walkthrough.shuffleHint,
}

// ---------------------------------------------------------------------------
// Emoji pairs per condition (universal across languages)
// ---------------------------------------------------------------------------

type EmojiConfig = {
    sliderLeft: string
    sliderRight: string
    toggleA: string
    toggleB: string
    sliderValue: number
}

const emojis: Record<ConditionKey, EmojiConfig> = {
    generic:    {sliderLeft: '😶', sliderRight: '😌', toggleA: '🌊', toggleB: '🏔', sliderValue: 0.75},
}

// ---------------------------------------------------------------------------
// Condition-specific content per language
// ---------------------------------------------------------------------------

type ConditionStrings = {
    descriptions: [string, string, string]
    writingPrompt: string
    writingSample: string
    acknowledgement: string
}

const conditionContent: Record<ConditionKey, Record<Lang, ConditionStrings>> = {

    // ── Generic ──────────────────────────────────────────────────────────

    generic: {
        en: {
            descriptions: [
                'A simple gesture captures where you are right now.',
                'A toggle and a few words — only if you want to.',
                'Everything you shared becomes a session shaped to your state.',
            ],
            writingPrompt: 'Anything on your mind right now?',
            writingSample: 'A strange restlessness, like waiting for something I can\'t name...',
            acknowledgement: 'I hear you. Your session is being written.',
        },
        fr: {
            descriptions: [
                'Un geste simple capte où vous en êtes maintenant.',
                'Un choix et quelques mots — seulement si vous le souhaitez.',
                'Tout ce que vous avez partagé devient une séance façonnée pour votre état.',
            ],
            writingPrompt: 'Quelque chose vous traverse en ce moment\u00a0?',
            writingSample: 'Une agitation étrange, comme attendre quelque chose que je ne saurais nommer...',
            acknowledgement: 'Je vous entends. Votre séance est en train de s\'écrire.',
        },
        de: {
            descriptions: [
                'Eine einfache Geste erfasst, wo Sie gerade stehen.',
                'Ein Schalter und ein paar Worte — nur wenn Sie möchten.',
                'Alles, was Sie geteilt haben, wird zu einer Sitzung für Ihren Zustand.',
            ],
            writingPrompt: 'Beschäftigt Sie gerade etwas?',
            writingSample: 'Eine seltsame Unruhe, als würde ich auf etwas warten, das ich nicht benennen kann...',
            acknowledgement: 'Ich höre Sie. Ihre Sitzung wird geschrieben.',
        },
        es: {
            descriptions: [
                'Un gesto simple capta dónde estás ahora mismo.',
                'Un selector y unas palabras — solo si quieres.',
                'Todo lo que compartiste se convierte en una sesión moldeada para tu estado.',
            ],
            writingPrompt: '¿Algo ronda tu mente ahora?',
            writingSample: 'Una inquietud extraña, como esperar algo que no puedo nombrar...',
            acknowledgement: 'Te escucho. Tu sesión se está escribiendo.',
        },
        ja: {
            descriptions: copy.ja.walkthrough.descriptions as [string, string, string],
            writingPrompt: copy.ja.walkthrough.writingPrompt,
            writingSample: copy.ja.walkthrough.writingSample,
            acknowledgement: copy.ja.walkthrough.acknowledgement,
        },
        zh: {
            descriptions: copy.zh.walkthrough.descriptions as [string, string, string],
            writingPrompt: copy.zh.walkthrough.writingPrompt,
            writingSample: copy.zh.walkthrough.writingSample,
            acknowledgement: copy.zh.walkthrough.acknowledgement,
        },
    },
}

// ---------------------------------------------------------------------------
// Assembly: combine emoji configs, frame titles, and condition strings
// ---------------------------------------------------------------------------

function buildContent(condition: ConditionKey, lang: Lang): WalkthroughContent {
    const e = emojis[condition]
    const titles = frameTitles[lang]
    const c = conditionContent[condition][lang]

    return {
        frames: [
            {
                title: titles[0],
                description: c.descriptions[0],
                content: {
                    type: 'slider',
                    leftEmoji: e.sliderLeft,
                    rightEmoji: e.sliderRight,
                    value: e.sliderValue,
                },
            },
            {
                title: titles[1],
                description: c.descriptions[1],
                content: {
                    type: 'toggleAndWriting',
                    optionA: e.toggleA,
                    optionB: e.toggleB,
                    selected: 'a',
                    writingPrompt: c.writingPrompt,
                    writingSample: c.writingSample,
                },
            },
            {
                title: titles[2],
                description: c.descriptions[2],
                content: {
                    type: 'delivery',
                    acknowledgement: c.acknowledgement,
                },
            },
        ],
        shuffleHint: shuffleHints[lang],
    }
}

const conditions: ConditionKey[] = ['generic']

export const walkthroughContent: Record<ConditionKey, Partial<Record<Lang, WalkthroughContent>>> =
    Object.fromEntries(
        conditions.map(condition => [
            condition,
            Object.fromEntries(
                (Object.keys(conditionContent[condition]) as Lang[]).map(lang => [lang, buildContent(condition, lang)])
            ),
        ])
    ) as Record<ConditionKey, Partial<Record<Lang, WalkthroughContent>>>
